import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { LeadDocumentUploader } from "@/components/leads/LeadDocumentUploader";
import { LeadInformation } from "@/components/leads/LeadInformation";
import { formatAccountType } from "@/lib/accountTypes";
import {
  Building2,
  Plus,
  Calendar,
  CheckCircle2,
  MessageSquare,
  FileText,
  Users,
  Edit,
  UserPlus,
  Briefcase,
  ArrowLeft,
  DollarSign,
  Clock,
  MapPin,
  Square,
  Trash2,
} from "lucide-react";
import { useState, useEffect } from "react";
import { EditLeadSheet } from "./EditLeadSheet";
import { AddTaskSheet } from "./AddTaskSheet";
import { EditTaskSheet } from "./EditTaskSheet";
import { AddLeadEventSheet } from "./AddLeadEventSheet";
import { EditLeadEventSheet } from "./EditLeadEventSheet";
import { LeadMeetings } from "./LeadMeetings";

import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { ProtectByPermission, PermissionButton } from "@/components/auth";
import { toast } from "sonner";
import { APIProvider } from "@vis.gl/react-google-maps";

interface Task {
  id: string;
  title: string;
  dueDate: string;
  status: "pending" | "completed";
  assignedTo: string;
  priority?: "low" | "medium" | "high";
}

interface Note {
  id: string;
  content: string;
  createdAt: string;
  createdBy: string;
}


export default function LeadView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [newNote, setNewNote] = useState("");
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isAddTaskSheetOpen, setIsAddTaskSheetOpen] = useState(false);
  const [isEditTaskSheetOpen, setIsEditTaskSheetOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isScheduleMeetingSheetOpen, setIsScheduleMeetingSheetOpen] = useState(false);
  const [isEditMeetingSheetOpen, setIsEditMeetingSheetOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Fetch lead data from businesses table - use "skip" pattern for conditional queries
  const lead = useQuery(
    api.businesses.getBusinessById,
    id ? { id: id as Id<"businesses"> } : "skip"
  );

  // Force refresh when returning to this page (duplicate query removed - lead already handles this)

  // Handle edit meeting
  const handleEditMeeting = (meeting: any) => {
    setSelectedMeeting(meeting);
    setIsEditMeetingSheetOpen(true);
  };

  // Fetch tasks for this lead - use "skip" pattern
  const tasksData = useQuery(
    api.leadTasks.getTasksByBusiness,
    id ? { businessId: id as Id<"businesses"> } : "skip"
  );
  const tasks = tasksData || [];

  // Fetch notes for this lead - use "skip" pattern
  const notesData = useQuery(
    api.leadNotes.getNotesByBusiness,
    id ? { businessId: id as Id<"businesses"> } : "skip"
  );
  const notes = notesData || [];

  // Fetch bids for this lead - use "skip" pattern
  const bidsData = useQuery(
    api.bids.getBidsByBusinessId,
    id ? { businessId: id as Id<"businesses"> } : "skip"
  );
  const bids = bidsData || [];

  // Create note mutation
  const createNote = useMutation(api.leadNotes.createNote);

  // Update task mutation
  const updateTask = useMutation(api.leadTasks.updateTask);

  // Delete task mutation
  const deleteTask = useMutation(api.leadTasks.deleteTask);

  // Delete note mutation
  const deleteNote = useMutation(api.leadNotes.deleteNote);

  // Convert lead to client mutation
  const updateBusiness = useMutation(api.businesses.update);
  const createLeadNote = useMutation(api.leadNotes.createNote);

  // Convert lead to client function
  const handleConvertToClient = async () => {
    if (!id || !lead) {
      toast.error("Lead not found");
      return;
    }

    try {
      // Update the business type from lead to client
      await updateBusiness({
        id: id as Id<"businesses">,
        type: "client",
        serviceStatus: "active",
      });

      // Add a note about the conversion
      await createLeadNote({
        businessId: id as Id<"businesses">,
        content: "Lead converted to client",
      });

      toast.success("Lead successfully converted to client!");

      // Navigate to the client view page
      navigate(`/clients/${id}`);
    } catch (error) {
      console.error("Error converting lead to client:", error);
      toast.error("Failed to convert lead to client. Please try again.");
    }
  };

  // Handle lead status update
  const handleStatusUpdate = async (newStatus: string) => {
    if (!id || !lead || newStatus === lead.status) return;

    setIsUpdatingStatus(true);
    try {
      await updateBusiness({
        id: id as Id<"businesses">,
        status: newStatus as "new" | "contacted" | "qualified" | "unqualified",
      });
      toast.success(
        `Lead status updated to ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`
      );
    } catch (error) {
      console.error("Error updating lead status:", error);
      toast.error("Failed to update lead status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Handle loading and error states
  useEffect(() => {
    if (lead !== undefined) {
      setIsLoading(false);
    }

    if (lead === null) {
      setError("Lead not found");
      setIsLoading(false);
    }
  }, [lead]);

  // Handle adding a new note
  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return;

    try {
      await createNote({
        businessId: id as Id<"businesses">,
        content: newNote,
      });
      setNewNote("");
    } catch (err) {
      console.error("Error creating note:", err);
      setError("Failed to create note");
    }
  };

  // Handle toggling task status
  const handleToggleTaskStatus = async (taskId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === "completed" ? "pending" : "completed";
      await updateTask({
        id: taskId as any,
        status: newStatus,
      });
    } catch (err) {
      console.error("Error updating task:", err);
      setError("Failed to update task");
    }
  };

  // Use the lead data directly without mapping
  const currentLead = lead || null;

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""}>
      <DashboardLayout activeTab="leads">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1"
                  onClick={() => (window.location.href = "/leads")}
                >
                  <ArrowLeft className="h-4 w-4" /> Back to Leads
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {currentLead?.name || "Loading..."}
                </h1>
                {currentLead?.status && (
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      {
                        new: "bg-blue-100 text-blue-700",
                        contacted: "bg-yellow-100 text-yellow-700",
                        qualified: "bg-green-100 text-green-700",
                        unqualified: "bg-gray-100 text-gray-700",
                      }[currentLead.status as "new" | "contacted" | "qualified" | "unqualified"] ||
                      "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {currentLead.status.charAt(0).toUpperCase() + currentLead.status.slice(1)}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 text-gray-500">
                <Building2 className="h-4 w-4" />
                <span>{formatAccountType(currentLead?.accountType)}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <ProtectByPermission permission="clients:write">
                <Button variant="outline" className="gap-2" onClick={handleConvertToClient}>
                  <UserPlus className="h-4 w-4" /> Convert To Client
                </Button>
              </ProtectByPermission>
              <ProtectByPermission permission="leads:write">
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => setIsScheduleMeetingSheetOpen(true)}
                >
                  <Calendar className="h-4 w-4" /> Schedule Meeting
                </Button>
              </ProtectByPermission>

              <ProtectByPermission permission="leads:write">
                <Button className="gap-2" onClick={() => setIsEditSheetOpen(true)}>
                  <Edit className="h-4 w-4" /> Edit Lead
                </Button>
              </ProtectByPermission>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {/* Left Column - Details */}
            <div className="space-y-6">
              <LeadInformation lead={currentLead} />
            </div>

            {/* Right Column - Tabs */}
            <div className="md:col-span-2">
              <Card className="p-6">
                <Tabs defaultValue="opportunity" className="w-full">
                  <TabsList className="mb-4 grid w-full grid-cols-6">
                    <TabsTrigger value="opportunity" className="gap-2">
                      <Briefcase className="h-4 w-4" /> Opportunity
                    </TabsTrigger>
                    <TabsTrigger value="bids" className="gap-2">
                      <DollarSign className="h-4 w-4" /> Bids
                    </TabsTrigger>
                    <TabsTrigger value="meetings" className="gap-2">
                      <Calendar className="h-4 w-4" /> Meetings
                    </TabsTrigger>
                    <TabsTrigger value="tasks" className="gap-2">
                      <CheckCircle2 className="h-4 w-4" /> Tasks
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="gap-2">
                      <MessageSquare className="h-4 w-4" /> Notes
                    </TabsTrigger>
                    <TabsTrigger value="files" className="gap-2">
                      <FileText className="h-4 w-4" /> Files
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="opportunity" className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Opportunity</h3>
                      {/* Status Dropdown */}
                      <ProtectByPermission permission="leads:write">
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-gray-700">Status:</label>
                          <Select
                            value={currentLead?.status || "new"}
                            onValueChange={handleStatusUpdate}
                            disabled={isUpdatingStatus}
                          >
                            <SelectTrigger
                              className={`h-8 w-36 ${isUpdatingStatus ? "opacity-50" : ""}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">
                                <span className="flex items-center gap-2">
                                  <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                                  New
                                </span>
                              </SelectItem>
                              <SelectItem value="contacted">
                                <span className="flex items-center gap-2">
                                  <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                                  Contacted
                                </span>
                              </SelectItem>
                              <SelectItem value="qualified">
                                <span className="flex items-center gap-2">
                                  <div className="h-2 w-2 rounded-full bg-green-500"></div>
                                  Qualified
                                </span>
                              </SelectItem>
                              <SelectItem value="unqualified">
                                <span className="flex items-center gap-2">
                                  <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                                  Unqualified
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {isUpdatingStatus && (
                            <span className="ml-1 text-xs text-gray-500">Updating...</span>
                          )}
                        </div>
                      </ProtectByPermission>
                    </div>

                    {isLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-700"></div>
                      </div>
                    ) : error ? (
                      <div className="py-8 text-center text-red-500">{error}</div>
                    ) : (
                      <div className="space-y-6">
                        {/* Lead Details Section - Always shows */}
                        <div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4 sm:grid-cols-4">
                          <div>
                            <p className="text-sm text-gray-500">Status</p>
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                {
                                  new: "bg-blue-100 text-blue-700",
                                  contacted: "bg-yellow-100 text-yellow-700",
                                  qualified: "bg-green-100 text-green-700",
                                  unqualified: "bg-gray-100 text-gray-700",
                                }[
                                  (currentLead?.status || "new") as
                                    | "new"
                                    | "contacted"
                                    | "qualified"
                                    | "unqualified"
                                ] || "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {(currentLead?.status || "new").charAt(0).toUpperCase() +
                                (currentLead?.status || "new").slice(1)}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Source</p>
                            <p className="font-medium">{currentLead?.source || "Unknown"}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Created Date</p>
                            <p className="font-medium">
                              {currentLead?.createdAt
                                ? new Date(currentLead.createdAt).toLocaleDateString()
                                : "Unknown"}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Estimated Value</p>
                            <p className="font-medium">
                              {currentLead?.estimatedValue
                                ? `$${currentLead.estimatedValue.toLocaleString()}`
                                : "Not specified"}
                            </p>
                          </div>
                        </div>

                        {/* Opportunity Details Section - Conditional */}
                        {!currentLead?.opportunityDescription &&
                        !currentLead?.budgetMin &&
                        !currentLead?.budgetMax &&
                        !currentLead?.employeeCount &&
                        !currentLead?.frequency &&
                        !currentLead?.potentialStartDate &&
                        (!currentLead?.requirements || currentLead.requirements.length === 0) ? (
                          <div className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-gray-500">
                            No opportunity details available. Edit this lead to add opportunity
                            information.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Opportunity Description */}
                            {currentLead?.opportunityDescription && (
                              <div>
                                <p className="mb-1 text-sm text-gray-500">Description</p>
                                <p className="text-gray-700">
                                  {currentLead.opportunityDescription}
                                </p>
                              </div>
                            )}

                            {/* Key Details Grid */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div className="space-y-3">
                                {(currentLead?.budgetMin || currentLead?.budgetMax) && (
                                  <div className="flex items-center gap-2">
                                    <DollarSign className="h-4 w-4 text-gray-400" />
                                    <div>
                                      <p className="text-sm text-gray-500">Budget Range</p>
                                      <p className="font-medium">
                                        {currentLead.budgetMin && currentLead.budgetMax
                                          ? `$${currentLead.budgetMin} - $${currentLead.budgetMax}`
                                          : currentLead.budgetMin
                                            ? `$${currentLead.budgetMin}+`
                                            : currentLead.budgetMax
                                              ? `Up to $${currentLead.budgetMax}`
                                              : "Not specified"}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                {currentLead?.frequency && (
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-gray-400" />
                                    <div>
                                      <p className="text-sm text-gray-500">Frequency</p>
                                      <p className="font-medium">{currentLead.frequency}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="space-y-3">
                                {currentLead?.employeeCount && (
                                  <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4 text-gray-400" />
                                    <div>
                                      <p className="text-sm text-gray-500">Employee Count</p>
                                      <p className="font-medium">{currentLead.employeeCount}</p>
                                    </div>
                                  </div>
                                )}
                                {currentLead?.potentialStartDate && (
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-gray-400" />
                                    <div>
                                      <p className="text-sm text-gray-500">Potential Start Date</p>
                                      <p className="font-medium">
                                        {new Date(
                                          currentLead.potentialStartDate
                                        ).toLocaleDateString()}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Estimated Square Footage */}
                            {currentLead?.estimatedSquareFootage && (
                              <div className="flex items-center gap-2">
                                <Square className="h-4 w-4 text-gray-400" />
                                <div>
                                  <p className="text-sm text-gray-500">Estimated Square Footage</p>
                                  <p className="font-medium">
                                    {currentLead.estimatedSquareFootage} sq ft
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Requirements */}
                            {currentLead?.requirements && currentLead.requirements.length > 0 && (
                              <div>
                                <p className="mb-2 text-sm text-gray-500">Requirements</p>
                                <div className="grid grid-cols-1 gap-2">
                                  {currentLead.requirements.map((req: string, index: number) => (
                                    <div key={index} className="flex items-start gap-2">
                                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                                      <span className="text-sm text-gray-600">{req}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="tasks" className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Tasks</h3>
                      <ProtectByPermission permission="leads:write">
                        <Button
                          size="sm"
                          className="gap-2"
                          onClick={() => setIsAddTaskSheetOpen(true)}
                        >
                          <Plus className="h-4 w-4" /> Add Task
                        </Button>
                      </ProtectByPermission>
                    </div>
                    {isLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-700"></div>
                      </div>
                    ) : error ? (
                      <div className="py-8 text-center text-red-500">{error}</div>
                    ) : tasks.length === 0 ? (
                      <EmptyState
                        icon={CheckCircle2}
                        title="No tasks yet"
                        description="Add a task to get started."
                      />
                    ) : (
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-4">
                          {tasks.map(
                            (task: {
                              _id: string;
                              title: string;
                              dueDate: string;
                              status: string;
                              assignedTo: string;
                              priority?: string;
                              createdByName?: string;
                            }) => (
                              <Card key={task._id} className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      {task.priority && (
                                        <span
                                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                            {
                                              low: "bg-blue-100 text-blue-700",
                                              medium: "bg-yellow-100 text-yellow-700",
                                              high: "bg-red-100 text-red-700",
                                            }[task.priority as "low" | "medium" | "high"] ||
                                            "bg-blue-100 text-blue-700"
                                          }`}
                                        >
                                          {task.priority.charAt(0).toUpperCase() +
                                            task.priority.slice(1)}
                                        </span>
                                      )}
                                      <button
                                        onClick={() =>
                                          handleToggleTaskStatus(task._id, task.status)
                                        }
                                        className="focus:outline-none"
                                      >
                                        <CheckCircle2
                                          className={`h-4 w-4 ${task.status === "completed" ? "text-green-500" : "text-gray-400"}`}
                                        />
                                      </button>
                                      <span className="font-medium">{task.title}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 text-sm text-gray-500">
                                      <div className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {task.dueDate}
                                      </div>
                                      {task.createdByName && (
                                        <div className="flex items-center gap-1">
                                          <span>Created by</span>
                                          <Users className="h-3 w-3" />
                                          <span>{task.createdByName}</span>
                                        </div>
                                      )}
                                      <div className="flex items-center gap-1">
                                        <span>Assigned to</span>
                                        <Users className="h-3 w-3" />
                                        <span>{task.assignedTo}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <ProtectByPermission permission="leads:write">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => {
                                          setSelectedTask({
                                            id: task._id,
                                            title: task.title,
                                            dueDate: task.dueDate,
                                            status: task.status as "pending" | "completed",
                                            assignedTo: task.assignedTo,
                                            priority: (task.priority || "low") as
                                              | "low"
                                              | "medium"
                                              | "high",
                                          });
                                          setIsEditTaskSheetOpen(true);
                                        }}
                                      >
                                        <Edit className="h-4 w-4" />
                                        <span className="sr-only">Edit</span>
                                      </Button>
                                    </ProtectByPermission>
                                    <ProtectByPermission permission="leads:delete">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => deleteTask({ id: task._id })}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        <span className="sr-only">Delete</span>
                                      </Button>
                                    </ProtectByPermission>
                                  </div>
                                </div>
                              </Card>
                            )
                          )}
                        </div>
                      </ScrollArea>
                    )}
                  </TabsContent>

                  <TabsContent value="notes" className="space-y-4">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Notes</h3>
                    </div>
                    <div className="space-y-4">
                      <Card className="p-4">
                        <Textarea
                          placeholder="Add a note..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          className="mb-4"
                        />
                        <ProtectByPermission permission="leads:write">
                          <Button className="gap-2" onClick={handleAddNote}>
                            <Plus className="h-4 w-4" /> Add Note
                          </Button>
                        </ProtectByPermission>
                      </Card>
                      {isLoading ? (
                        <div className="flex justify-center py-8">
                          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-700"></div>
                        </div>
                      ) : error ? (
                        <div className="py-8 text-center text-red-500">{error}</div>
                      ) : notes.length === 0 ? (
                        <EmptyState
                          icon={MessageSquare}
                          title="No notes yet"
                          description="Add a note to get started."
                        />
                      ) : (
                        <ScrollArea className="h-[300px] pr-4">
                          <div className="space-y-4">
                            {notes.map(
                              (note: {
                                _id: string;
                                content: string;
                                createdAt: number;
                                createdBy: string;
                                createdByName?: string;
                              }) => (
                                <Card key={note._id} className="p-4">
                                  <div className="space-y-2">
                                    <div className="flex justify-between">
                                      <p className="text-gray-600">{note.content}</p>
                                      <ProtectByPermission permission="leads:delete">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={() => deleteNote({ id: note._id })}
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <path d="M3 6h18"></path>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                          </svg>
                                        </Button>
                                      </ProtectByPermission>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                      <span>Created by</span>
                                      <Users className="h-3 w-3" />
                                      <span>
                                        {note.createdByName || note.createdBy || "Unknown"}
                                      </span>
                                      <span>•</span>
                                      <span>{new Date(note.createdAt).toLocaleString()}</span>
                                    </div>
                                  </div>
                                </Card>
                              )
                            )}
                          </div>
                        </ScrollArea>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="files" className="space-y-4">
                    {id && <LeadDocumentUploader businessId={id as Id<"businesses">} />}
                  </TabsContent>

                  <TabsContent value="meetings" className="space-y-4">
                    {id && (
                      <LeadMeetings
                        businessId={id as Id<"businesses">}
                        onScheduleMeeting={() => setIsScheduleMeetingSheetOpen(true)}
                        onEditMeeting={handleEditMeeting}
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="bids" className="space-y-4">
                    <div className="space-y-4">
                      {bids.length === 0 ? (
                        <EmptyState
                          icon={DollarSign}
                          title="No bids yet"
                          description="No bids have been created for this lead."
                        />
                      ) : (
                        <div className="space-y-3">
                          {bids.map(
                            (bid: {
                              _id: string;
                              title: string;
                              status: string;
                              createdAt: number;
                            }) => (
                              <Card key={bid._id} className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <h3 className="font-medium text-gray-900">{bid.title}</h3>
                                    <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                                      <span>
                                        Status:{" "}
                                        <span
                                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                            bid.status === "Approved"
                                              ? "bg-green-100 text-green-700"
                                              : bid.status === "Submitted"
                                                ? "bg-blue-100 text-blue-700"
                                                : bid.status === "In Review"
                                                  ? "bg-yellow-100 text-yellow-700"
                                                  : bid.status === "Rejected"
                                                    ? "bg-red-100 text-red-700"
                                                    : "bg-gray-100 text-gray-700"
                                          }`}
                                        >
                                          {bid.status}
                                        </span>
                                      </span>
                                      <span>
                                        Created: {new Date(bid.createdAt).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      navigate(`/bids/${bid._id}`, {
                                        state: { from: `/leads/${id}` },
                                      })
                                    }
                                  >
                                    View Bid
                                  </Button>
                                </div>
                              </Card>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            </div>
          </div>
        </div>
        <EditLeadSheet
          isOpen={isEditSheetOpen}
          onClose={() => setIsEditSheetOpen(false)}
          lead={currentLead}
        />
        <AddTaskSheet
          isOpen={isAddTaskSheetOpen}
          onClose={() => setIsAddTaskSheetOpen(false)}
          businessId={id as Id<"businesses">}
          onTaskAdded={() => {
            // This is a simple way to force a component re-render
            // which will trigger a refetch of the tasks
            setIsLoading(true);
            setTimeout(() => setIsLoading(false), 100);
          }}
        />
        {selectedTask && (
          <EditTaskSheet
            isOpen={isEditTaskSheetOpen}
            onClose={() => setIsEditTaskSheetOpen(false)}
            task={selectedTask}
            onTaskUpdated={() => {
              // This is a simple way to force a component re-render
              // which will trigger a refetch of the tasks
              setIsLoading(true);
              setTimeout(() => setIsLoading(false), 100);
            }}
          />
        )}
        <AddLeadEventSheet
          isOpen={isScheduleMeetingSheetOpen}
          onClose={() => setIsScheduleMeetingSheetOpen(false)}
          businessId={id as Id<"businesses">}
          defaultAddress={currentLead?.address}
          onEventAdded={() => {
            setIsScheduleMeetingSheetOpen(false);
            // Optionally refresh data or show success message
          }}
        />
        <EditLeadEventSheet
          isOpen={isEditMeetingSheetOpen}
          onClose={() => {
            setIsEditMeetingSheetOpen(false);
            setSelectedMeeting(null);
          }}
          event={selectedMeeting}
          onEventUpdated={() => {
            setIsEditMeetingSheetOpen(false);
            setSelectedMeeting(null);
            // Optionally refresh data or show success message
          }}
        />
      </DashboardLayout>
    </APIProvider>
  );
}
